import { useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useIntl } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { createSlug } from '../../util/urlHelpers';
import { formatMoney } from '../../util/currency';
import { ensureUser } from '../../util/data';
import { useLoading } from '../../context/loadingContext';

const HomeFeaturedEnhancerComponent = () => {
  const [featuredListings, setFeaturedListings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const intl = useIntl();
  const config = useConfiguration();
  const { setLoadingComplete } = useLoading();

  // Load featured listings when component mounts
  useEffect(() => {
    const fetchFeaturedListings = async () => {
      setIsLoading(true);
      try {
        // Call our API endpoint to fetch featured listings
        const apiBaseUrl = process.env.NODE_ENV === 'development'
          ? `http://localhost:${process.env.REACT_APP_DEV_API_SERVER_PORT}`
          : '';

        const response = await fetch(`${apiBaseUrl}/api/featured-listing/query`);

        if (!response.ok) {
          throw new Error(`API returned ${response.status}`);
        }

        const data = await response.json();
        const listings = data.data || [];
        const included = data.included || [];

        // Build a lookup map for included resources (images and authors)
        const includedMap = {};
        included.forEach(resource => {
          const id = resource.id?.uuid || resource.id;
          includedMap[id] = resource;
        });

        // Resolve image and author references for each listing
        const resolvedListings = listings.map(listing => {
          const resolved = { ...listing };

          // Resolve images
          if (listing.relationships?.images?.data) {
            resolved.images = listing.relationships.images.data
              .map(imgRef => {
                const imgId = imgRef.id?.uuid || imgRef.id;
                return includedMap[imgId];
              })
              .filter(Boolean);
          }

          // Resolve author
          if (listing.relationships?.author?.data) {
            const authorId = listing.relationships.author.data.id?.uuid || listing.relationships.author.data.id;
            resolved.author = includedMap[authorId];
          }

          return resolved;
        });

        setFeaturedListings(resolvedListings);
      } catch (error) {
        console.error('Failed to fetch featured listings:', error);
        setFeaturedListings([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeaturedListings();
  }, []);

  useEffect(() => {
    // Only run when we have listings loaded
    if (isLoading || featuredListings.length === 0) {
      return;
    }

    const enhanceHomeFeatured = () => {
      const homeFeaturedSection = document.getElementById('home-featured');

      if (!homeFeaturedSection) {
        return;
      }

      // Center align the title and description using CSS injection
      if (!document.querySelector('style[data-featured-center-styles]')) {
        const centerStyle = document.createElement('style');
        centerStyle.setAttribute('data-featured-center-styles', 'true');
        centerStyle.textContent = `
          @media (max-width: 768px) {
            #home-featured h2.SectionBuilder_title__fPMOM {
              width: 100% !important;
              text-align: center !important;
            }
            #home-featured p.SectionBuilder_description__A7dhl {
              width: 100% !important;
              text-align: center !important;
            }
          }
        `;
        document.head.appendChild(centerStyle);
      }

      // Filter for published and non-deleted listings
      // The API already filters for featuredEndDate > now, so we don't need to check isFeaturedListing
      const validFeaturedListings = featuredListings.filter(listing => {
        const isPublished = listing?.attributes?.state === 'published';
        const notDeleted = !listing?.attributes?.deleted;
        return isPublished && notDeleted;
      }).slice(0, 8); // Limit to 8 featured listings

      // Check if we have an existing container
      const existingContainer = homeFeaturedSection.querySelector('[data-featured-enhanced="true"]');

      // If we already have a container, check if we should update it
      if (existingContainer) {
        if (validFeaturedListings.length > 0) {
          existingContainer.remove();
        } else {
          return;
        }
      }

      if (validFeaturedListings.length === 0) {
        // Remove any existing containers first
        const existingContainer = homeFeaturedSection.querySelector('[data-featured-enhanced="true"]');
        if (existingContainer) {
          existingContainer.remove();
        }

        // Show skeleton loading if we're still searching, otherwise show no listings message
        if (isLoading) {
          renderSkeletonLoading(homeFeaturedSection);
        } else {
          renderNoFeaturedListings(homeFeaturedSection);
        }
        // Single requestAnimationFrame to prevent iOS render conflicts
        requestAnimationFrame(() => {
          setLoadingComplete('homeFeatured');
        });
        return;
      }

      // Create the featured listings container
      const featuredContainer = document.createElement('div');
      featuredContainer.setAttribute('data-featured-enhanced', 'true');
      featuredContainer.className = 'featured-container-mobile';
      featuredContainer.style.cssText = `
        padding: 0;
        margin-top: 2rem;
      `;

      // Create carousel container
      const carouselWrapper = document.createElement('div');
      carouselWrapper.className = 'carousel-wrapper-mobile';
      carouselWrapper.style.cssText = `
        position: relative;
        max-width: 1200px;
        margin: 0 auto;
        overflow: hidden;
      `;

      // Create scrollable grid container
      const gridContainer = document.createElement('div');
      gridContainer.style.cssText = `
        display: flex;
        gap: 1rem;
        overflow-x: auto;
        padding-bottom: 10px;
        scrollbar-width: thin;
        scrollbar-color: var(--marketplaceColor, #1B5E20) transparent;
        -webkit-overflow-scrolling: touch;
        user-select: none;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
      `;
      
      // Prevent text selection during drag (desktop only)
      if (!('ontouchstart' in window)) {
        gridContainer.addEventListener('selectstart', (e) => e.preventDefault());
        gridContainer.addEventListener('dragstart', (e) => e.preventDefault());
      }

      // Add custom scrollbar and drag styles (only once)
      if (!document.querySelector('style[data-featured-carousel-styles]')) {
        const style = document.createElement('style');
        style.setAttribute('data-featured-carousel-styles', 'true');
        style.textContent = `
        .featured-listings-carousel::-webkit-scrollbar {
          height: 8px;
        }
        .featured-listings-carousel::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        .featured-listings-carousel::-webkit-scrollbar-thumb {
          background: var(--marketplaceColor, #1B5E20);
          border-radius: 4px;
        }
        .featured-listings-carousel::-webkit-scrollbar-thumb:hover {
          background: #2E7D32;
        }
        .featured-listings-carousel.dragging {
          scroll-behavior: auto !important;
        }
        .featured-listings-carousel.dragging * {
          pointer-events: none;
        }
        
        /* Tablet portrait mode: reduce entire section by 30% */
        @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
          #home-featured {
            transform: scale(0.7) !important;
            transform-origin: center top !important;
            margin-bottom: -30% !important;
          }
          .featured-listings-carousel .featured-card {
            flex: 0 0 200px !important;
            min-width: 200px !important;
            max-width: 200px !important;
            width: 200px !important;
          }
        }

        @media (max-width: 768px) {
          .featured-container-mobile {
            padding: 0 16px !important;
            margin: 2rem 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
          .carousel-wrapper-mobile {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
            box-sizing: border-box !important;
          }
          .featured-listings-carousel {
            gap: 16px !important;
            padding: 0 !important;
            margin: 0 !important;
            scroll-behavior: smooth !important;
            scroll-snap-type: x mandatory !important;
            -webkit-overflow-scrolling: touch !important;
          }
          .featured-listings-carousel .featured-card {
            flex: 0 0 calc(100% - 16px) !important;
            min-width: calc(100% - 16px) !important;
            max-width: calc(100% - 16px) !important;
            width: calc(100% - 16px) !important;
            scroll-snap-align: start !important;
          }
        }
      `;
        document.head.appendChild(style);
      }
      gridContainer.className = 'featured-listings-carousel';

      // Find the section content area to insert the listings
      let sectionContent = homeFeaturedSection.querySelector('.SectionContainer_sectionContent__vqzb1');
      if (!sectionContent) {
        sectionContent = homeFeaturedSection.querySelector('[class*="sectionContent"]');
      }
      if (!sectionContent) {
        sectionContent = homeFeaturedSection;
      }

      // Add the containers to the section
      carouselWrapper.appendChild(gridContainer);
      featuredContainer.appendChild(carouselWrapper);
      sectionContent.appendChild(featuredContainer);

      // Render the real featured listings cards
      renderFeaturedListingsCards(validFeaturedListings, gridContainer, intl, config, history);

      // Center listings if they don't fill the container width
      centerListingsIfNeeded(gridContainer, validFeaturedListings.length);

      // Add navigation dots for mobile
      addMobileNavigationDots(carouselWrapper, gridContainer, validFeaturedListings.length);

      // Single requestAnimationFrame with timeout for stability
      requestAnimationFrame(() => {
        setTimeout(() => {
          setLoadingComplete('homeFeatured');
        }, 100); // Reduced delay
      });
    };

    // Run immediately and after a short delay to ensure the section exists
    const runEnhancement = () => {
      enhanceHomeFeatured();
      // Also signal completion after timeout in case the above didn't complete
      setTimeout(() => setLoadingComplete('homeFeatured'), 100);
    };

    runEnhancement();
    const timeoutId = setTimeout(runEnhancement, 500);

    // Add resize listener to recalculate centering on window resize
    const handleResize = () => {
      const gridContainer = document.querySelector('.featured-listings-carousel');
      if (gridContainer) {
        const listingCount = gridContainer.children.length;
        centerListingsIfNeeded(gridContainer, listingCount);
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      // Clean up resize listener
      window.removeEventListener('resize', handleResize);
    };
  }, [featuredListings, isLoading]); // Only re-run when listings change or loading state changes

  return null; // This component doesn't render anything directly
};

// Helper function to add mobile navigation dots
const addMobileNavigationDots = (carouselWrapper, gridContainer, totalItems) => {
  if (totalItems <= 1) return; // No dots needed for single item

  const dotsContainer = document.createElement('div');
  dotsContainer.className = 'carousel-dots';
  dotsContainer.style.cssText = `
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    margin-top: 1rem;
    padding: 0 1rem;
  `;

  // Hide dots on desktop
  const dotsStyle = document.createElement('style');
  dotsStyle.textContent = `
    .carousel-dots {
      display: none;
    }
    @media (max-width: 768px) {
      .carousel-dots {
        display: flex !important;
      }
    }
    .carousel-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #ccc;
      cursor: pointer;
      transition: background 0.2s ease;
    }
    .carousel-dot.active {
      background: var(--marketplaceColor, #1B5E20);
    }
  `;
  document.head.appendChild(dotsStyle);

  // Create dots
  for (let i = 0; i < totalItems; i++) {
    const dot = document.createElement('div');
    dot.className = `carousel-dot ${i === 0 ? 'active' : ''}`;
    dot.addEventListener('click', () => {
      // Calculate scroll position for any screen size
      const cardWidth = gridContainer.children[0]?.offsetWidth + 16;
      gridContainer.scrollTo({
        left: i * cardWidth,
        behavior: 'smooth'
      });
      updateActiveDot(dotsContainer, i);
    });
    dotsContainer.appendChild(dot);
  }

  carouselWrapper.appendChild(dotsContainer);

  // Optimized scroll event handling for dots (mobile-friendly)
  let scrollTimeout;
  let isScrollingDots = false;

  gridContainer.addEventListener('scroll', () => {
    if (isScrollingDots) return;

    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => {
      if (!isScrollingDots) {
        isScrollingDots = true;
        requestAnimationFrame(() => {
          try {
            const cardWidth = gridContainer.children[0]?.offsetWidth + 16;
            const scrollLeft = gridContainer.scrollLeft;
            const activeIndex = Math.round(scrollLeft / cardWidth);
            updateActiveDot(dotsContainer, Math.max(0, Math.min(activeIndex, totalItems - 1)));
          } finally {
            isScrollingDots = false;
          }
        });
      }
    }, 200); // Longer delay for mobile stability
  }, { passive: true });
};

// Helper function to update active dot
const updateActiveDot = (dotsContainer, activeIndex) => {
  const dots = dotsContainer.querySelectorAll('.carousel-dot');
  dots.forEach((dot, index) => {
    dot.classList.toggle('active', index === activeIndex);
  });
};

// Helper function to center listings if they don't fill the container width
const centerListingsIfNeeded = (gridContainer, listingCount) => {
  // Use requestAnimationFrame to ensure DOM is fully rendered
  requestAnimationFrame(() => {
    const containerWidth = gridContainer.offsetWidth;
    const firstCard = gridContainer.children[0];
    
    if (!firstCard || listingCount === 0) return;
    
    const cardWidth = firstCard.offsetWidth;
    const gap = 16; // 1rem gap between cards
    const totalContentWidth = (cardWidth * listingCount) + (gap * (listingCount - 1));
    
    // Only center if content doesn't fill the container
    if (totalContentWidth < containerWidth) {
      const availableSpace = containerWidth - totalContentWidth;
      const leftPadding = availableSpace / 2;
      
      // Apply centering by adding left padding
      gridContainer.style.paddingLeft = `${leftPadding}px`;
      gridContainer.style.justifyContent = 'flex-start'; // Ensure cards start from the left padding
    } else {
      // Reset to default if content fills container
      gridContainer.style.paddingLeft = '0';
      gridContainer.style.justifyContent = 'flex-start';
    }
  });
};

// Helper function to create listing cards with real listing data
const renderFeaturedListingsCards = (listings, container, intl, config, history) => {
  if (!listings || !Array.isArray(listings) || !container) {
    return;
  }

  listings.forEach(listing => {
    const { title = '', price } = listing.attributes;

    // Images might be in different places depending on API response structure
    let firstImage = null;
    if (listing.images && listing.images.length > 0) {
      firstImage = listing.images[0];
    } else if (listing.relationships?.images?.data?.length > 0) {
      firstImage = listing.relationships.images.data[0];
    }

    // Author might also be in different places
    let authorName = 'Unknown Seller';
    if (listing.author) {
      const author = ensureUser(listing.author);
      authorName = author?.attributes?.profile?.displayName || 'Unknown Seller';
    } else if (listing.relationships?.author?.data) {
      authorName = 'Seller'; // We don't have full author data
    }

    // Detect tablet portrait mode for smaller card sizing
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const isTabletPortrait = screenWidth >= 768 && screenWidth <= 1024 && screenHeight > screenWidth;
    // Make cards much smaller on tablet portrait to show 2-3 at once instead of just 1
    const cardWidth = isTabletPortrait ? '200px' : 'min(280px, 75vw)';

    // Create listing card element
    const cardElement = document.createElement('div');
    cardElement.className = 'featured-card';
    cardElement.style.cssText = `
      background: white;
      border-radius: 12px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      overflow: hidden;
      cursor: pointer;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
      position: relative;
      flex: 0 0 ${cardWidth};
      min-width: ${cardWidth};
      height: auto;
      user-select: none;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
    `;

    // Add hover effects (disabled on touch devices to prevent iOS scroll conflicts)
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (!isTouchDevice) {
      cardElement.onmouseover = () => {
        cardElement.style.transform = 'translateY(-4px)';
        cardElement.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
      };
      cardElement.onmouseout = () => {
        cardElement.style.transform = 'translateY(0)';
        cardElement.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
      };
    }

    // Click handler - use native navigation since we're in DOM manipulation
    cardElement.style.cursor = 'pointer';
    cardElement.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Only navigate if we're not dragging
      if (!container.classList.contains('dragging')) {
        const slug = createSlug(title);
        const listingId = listing.id?.uuid || listing.id;
        const listingPath = `/l/${slug}/${listingId}`;

        // Use window.location for navigation
        window.location.href = listingPath;
      }
    });

    // Get image URL from variants
    let imageUrl = null;
    if (firstImage?.attributes?.variants) {
      imageUrl = firstImage.attributes.variants['listing-card']?.url || 
                firstImage.attributes.variants['listing-card-2x']?.url ||
                firstImage.attributes.variants['scaled-small']?.url ||
                firstImage.attributes.variants['scaled-medium']?.url ||
                Object.values(firstImage.attributes.variants)[0]?.url;
    }

    // Format price - need to ensure it's a proper Money type
    let priceDisplay = 'Make an offer';
    if (price) {
      try {
        // Price might be a plain object from API, format it manually
        if (price.amount && price.currency) {
          const amount = price.amount / 100; // Convert cents to dollars
          priceDisplay = new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: price.currency
          }).format(amount);
        }
      } catch (e) {
        console.warn('Failed to format price:', e);
      }
    }

    // Create featured badge using the actual FeaturedBadge component
    const featuredBadgeContainer = document.createElement('div');
    featuredBadgeContainer.style.cssText = `
      position: absolute;
      top: 8px;
      left: 8px;
      z-index: 2;
    `;
    featuredBadgeContainer.innerHTML = `
      <div style="
        display: inline-flex;
        align-items: center;
        padding: 3px 8px;
        background: var(--marketplaceColor, #1B5E20);
        color: white;
        border-radius: 9px;
        font-size: 10px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        white-space: nowrap;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
      ">Featured</div>
    `;

    // Create image container
    const imageContainer = document.createElement('div');
    imageContainer.style.cssText = `
      position: relative;
      aspect-ratio: 1;
      overflow: hidden;
    `;
    
    // Add featured badge
    imageContainer.appendChild(featuredBadgeContainer);
    
    if (imageUrl) {
      const img = document.createElement('img');
      img.src = imageUrl;
      img.alt = title;
      img.style.cssText = `
        width: 100%;
        height: 100%;
        object-fit: cover;
        background-color: #f5f5f5;
      `;
      
      // Fallback for failed images
      img.onerror = () => {
        img.style.display = 'none';
        const fallback = document.createElement('div');
        fallback.style.cssText = `
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #1B5E20, #2E7D32);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: min(48px, 12vw);
          font-weight: bold;
        `;
        fallback.textContent = '🏌️';
        imageContainer.appendChild(fallback);
      };
      
      imageContainer.appendChild(img);
    } else {
      // No image available
      const fallback = document.createElement('div');
      fallback.style.cssText = `
        width: 100%;
        height: 100%;
        background: linear-gradient(135deg, #1B5E20, #2E7D32);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: min(48px, 12vw);
        font-weight: bold;
      `;
      fallback.textContent = '🏌️';
      imageContainer.appendChild(fallback);
    }
    
    // Create content container
    const contentContainer = document.createElement('div');
    contentContainer.style.cssText = `padding: min(16px, 4vw);`;
    
    // Price display
    const priceDiv = document.createElement('div');
    priceDiv.style.cssText = `
      font-size: min(18px, 4.5vw);
      font-weight: 600;
      color: var(--marketplaceColor, #1B5E20);
      line-height: 1.2;
      margin-bottom: 8px;
    `;
    priceDiv.textContent = priceDisplay;
    
    // Title
    const titleH3 = document.createElement('h3');
    titleH3.style.cssText = `
      font-size: min(16px, 4vw);
      font-weight: 600;
      color: #333;
      margin: 0 0 8px 0;
      line-height: 1.3;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    `;
    titleH3.textContent = title;
    
    // Author
    const authorDiv = document.createElement('div');
    authorDiv.style.cssText = `
      font-size: min(14px, 3.5vw);
      color: #666;
      margin-top: 8px;
    `;
    authorDiv.textContent = `by ${authorName}`;
    
    // Assemble the card
    contentContainer.appendChild(priceDiv);
    contentContainer.appendChild(titleH3);
    contentContainer.appendChild(authorDiv);
    
    cardElement.appendChild(imageContainer);
    cardElement.appendChild(contentContainer);
    container.appendChild(cardElement);
  });

  // Mobile responsiveness is now handled via CSS with min() functions

  // Check if touch device to avoid conflicting scroll behaviors
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Declare variables outside the if/else blocks to avoid hoisting issues
  let touchStartTime;
  let touchMoved = false;
  let isScrolling = false;
  let startX;
  let scrollLeft;

  if (isTouchDevice) {
    // Mobile: Use native scroll with minimal interference

    container.addEventListener('touchstart', () => {
      touchStartTime = Date.now();
      touchMoved = false;
    }, { passive: true });

    container.addEventListener('touchmove', () => {
      touchMoved = true;
    }, { passive: true });

    container.addEventListener('touchend', () => {
      // Only prevent clicks if there was significant movement
      if (touchMoved && Date.now() - touchStartTime > 150) {
        container.classList.add('dragging');
        setTimeout(() => {
          container.classList.remove('dragging');
        }, 100); // Shorter timeout
      } else {
        // Ensure dragging class is removed on tap
        container.classList.remove('dragging');
      }
    }, { passive: true });
  } else {
    // Desktop: Add drag scrolling functionality

    container.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return; // Only left mouse button
      isScrolling = true;
      startX = e.pageX - container.offsetLeft;
      scrollLeft = container.scrollLeft;
      container.style.cursor = 'grabbing';
      e.preventDefault();
    });

    container.addEventListener('mouseleave', () => {
      if (isScrolling) {
        isScrolling = false;
        container.classList.remove('dragging');
        container.style.cursor = 'grab';
      }
    });

    container.addEventListener('mouseup', () => {
      if (isScrolling) {
        isScrolling = false;
        container.style.cursor = 'grab';
        setTimeout(() => {
          container.classList.remove('dragging');
        }, 50);
      } else {
        // Ensure dragging class is removed on click
        container.classList.remove('dragging');
      }
    });

    container.addEventListener('mousemove', (e) => {
      if (!isScrolling) return;
      e.preventDefault();
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX) * 1.5;
      container.scrollLeft = scrollLeft - walk;

      if (Math.abs(walk) > 3) {
        container.classList.add('dragging');
      }
    });

    container.style.cursor = 'grab';
    container.addEventListener('dragstart', (e) => e.preventDefault());
  }
};

// Helper function to show skeleton loading while searching for featured listings
const renderSkeletonLoading = (homeFeaturedSection) => {
  const featuredContainer = document.createElement('div');
  featuredContainer.setAttribute('data-featured-enhanced', 'true');
  featuredContainer.className = 'featured-container-mobile';
  featuredContainer.style.cssText = `
    padding: 0 20px;
    margin-top: 2rem;
  `;

  // Create carousel wrapper
  const carouselWrapper = document.createElement('div');
  carouselWrapper.className = 'carousel-wrapper-mobile';
  carouselWrapper.style.cssText = `
    position: relative;
    max-width: 1200px;
    margin: 0 auto;
    overflow: hidden;
  `;

  // Create skeleton grid container
  const gridContainer = document.createElement('div');
  gridContainer.className = 'featured-listings-carousel';
  gridContainer.style.cssText = `
    display: flex;
    gap: 1rem;
    overflow-x: auto;
    padding-bottom: 10px;
    scrollbar-width: thin;
    scrollbar-color: var(--marketplaceColor, #1B5E20) transparent;
    -webkit-overflow-scrolling: touch;
  `;

  // Add skeleton styles
  if (!document.querySelector('style[data-skeleton-styles]')) {
    const skeletonStyle = document.createElement('style');
    skeletonStyle.setAttribute('data-skeleton-styles', 'true');
    skeletonStyle.textContent = `
      .skeleton-card {
        background: white;
        border-radius: 12px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        overflow: hidden;
        position: relative;
        flex: 0 0 min(280px, 75vw);
        min-width: min(280px, 75vw);
        height: 423px;
        animation: skeleton-pulse 1.5s ease-in-out infinite;
      }
      
      .skeleton-image {
        width: 100%;
        height: 280px;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: skeleton-shimmer 2s infinite;
      }
      
      .skeleton-content {
        padding: 16px;
        height: 143px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      }
      
      .skeleton-price {
        width: 60%;
        height: 24px;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: skeleton-shimmer 2s infinite;
        border-radius: 4px;
        margin-bottom: 12px;
      }
      
      .skeleton-title {
        width: 95%;
        height: 20px;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: skeleton-shimmer 2s infinite;
        border-radius: 4px;
        margin-bottom: 12px;
      }
      
      .skeleton-author {
        width: 75%;
        height: 16px;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: skeleton-shimmer 2s infinite;
        border-radius: 4px;
        margin-top: auto;
      }
      
      @keyframes skeleton-pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.8; }
      }
      
      @keyframes skeleton-shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      
      @media (max-width: 768px) {
        .skeleton-card {
          flex: 0 0 calc(100% - 16px) !important;
          min-width: calc(100% - 16px) !important;
          max-width: calc(100% - 16px) !important;
          width: calc(100% - 16px) !important;
        }
      }
    `;
    document.head.appendChild(skeletonStyle);
  }

  // Create 4 skeleton cards
  for (let i = 0; i < 4; i++) {
    const skeletonCard = document.createElement('div');
    skeletonCard.className = 'skeleton-card';
    
    const skeletonImage = document.createElement('div');
    skeletonImage.className = 'skeleton-image';
    
    const skeletonContent = document.createElement('div');
    skeletonContent.className = 'skeleton-content';
    
    const skeletonPrice = document.createElement('div');
    skeletonPrice.className = 'skeleton-price';
    
    const skeletonTitle = document.createElement('div');
    skeletonTitle.className = 'skeleton-title';
    
    const skeletonAuthor = document.createElement('div');
    skeletonAuthor.className = 'skeleton-author';
    
    skeletonContent.appendChild(skeletonPrice);
    skeletonContent.appendChild(skeletonTitle);
    skeletonContent.appendChild(skeletonAuthor);
    
    skeletonCard.appendChild(skeletonImage);
    skeletonCard.appendChild(skeletonContent);
    gridContainer.appendChild(skeletonCard);
  }

  carouselWrapper.appendChild(gridContainer);
  featuredContainer.appendChild(carouselWrapper);

  let sectionContent = homeFeaturedSection.querySelector('.SectionContainer_sectionContent__vqzb1');
  if (!sectionContent) {
    sectionContent = homeFeaturedSection.querySelector('[class*="sectionContent"]');
  }
  if (!sectionContent) {
    sectionContent = homeFeaturedSection;
  }

  sectionContent.appendChild(featuredContainer);
};

// Helper function to show message when no featured listings are found
const renderNoFeaturedListings = (homeFeaturedSection) => {
  const featuredContainer = document.createElement('div');
  featuredContainer.setAttribute('data-featured-enhanced', 'true');
  featuredContainer.style.cssText = `
    padding: 0 20px;
    margin-top: 2rem;
    text-align: center;
  `;

  featuredContainer.innerHTML = `
    <div style="
      padding: 2rem;
      background: #f9f9f9;
      border-radius: 12px;
      color: #666;
      font-style: italic;
    ">
      No featured listings available at the moment. Create a listing and mark it as featured to see it here!
    </div>
  `;

  let sectionContent = homeFeaturedSection.querySelector('.SectionContainer_sectionContent__vqzb1');
  if (!sectionContent) {
    sectionContent = homeFeaturedSection.querySelector('[class*="sectionContent"]');
  }
  if (!sectionContent) {
    sectionContent = homeFeaturedSection;
  }

  sectionContent.appendChild(featuredContainer);
};

export default HomeFeaturedEnhancerComponent;